/**
 * Self-hosted font declarations (ADR-20260917-jh4s, supersedes
 * ADR-20260821-swpq's font specifics).
 *
 * The portal's font stack — declared once, in `tokens.ts`, as
 * `"Calibri", "Carlito", "Segoe UI", system-ui, sans-serif` — resolves in
 * that order. This file backs the first three names with `@font-face`
 * rules so the stack is explicit and auditable, never with a Google
 * Fonts-hosted `<link>` or `@import` of any kind, and never as an npm
 * package dependency.
 *
 * Only Carlito ships an actual file. FRP holds redistribution rights for
 * Carlito (SIL Open Font License 1.1 — see public/fonts/NOTICE.md) but not
 * for Calibri or Segoe UI, both Microsoft-licensed. Those two are declared
 * `local()`-only: a visitor with the real font already installed (most
 * Windows/Office machines) renders it; everyone else falls through to the
 * self-hosted, metric-compatible Carlito face below. No network request is
 * made for either — `local()` never fetches.
 *
 * No legacy icon webfont is declared here. Material UI 6's own icons and
 * this feature's own `Icon` component (ADR-20260917-3qv1) are inline SVG,
 * not a font — see design-system/icons.ts and components/Icon.tsx.
 */

/* Calibri — local-only. No redistribution rights; no file shipped. */
@font-face {
  font-family: "Calibri";
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src:
    local("Calibri"),
    local("Calibri Regular");
}

@font-face {
  font-family: "Calibri";
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: local("Calibri Bold");
}

@font-face {
  font-family: "Calibri";
  font-style: italic;
  font-weight: 400;
  font-display: swap;
  src: local("Calibri Italic");
}

/* Carlito — self-hosted, SIL OFL 1.1. Served from the portal's own origin
   (public/fonts/), never from a CDN. */
@font-face {
  font-family: "Carlito";
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url("/fonts/Carlito-Regular.ttf") format("truetype");
}

@font-face {
  font-family: "Carlito";
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url("/fonts/Carlito-Bold.ttf") format("truetype");
}

@font-face {
  font-family: "Carlito";
  font-style: italic;
  font-weight: 400;
  font-display: swap;
  src: url("/fonts/Carlito-Italic.ttf") format("truetype");
}

@font-face {
  font-family: "Carlito";
  font-style: italic;
  font-weight: 700;
  font-display: swap;
  src: url("/fonts/Carlito-BoldItalic.ttf") format("truetype");
}

/* Segoe UI — local-only, same reasoning as Calibri: no redistribution
   rights; no file shipped. */
@font-face {
  font-family: "Segoe UI";
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src:
    local("Segoe UI"),
    local("Segoe UI Regular");
}

@font-face {
  font-family: "Segoe UI";
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: local("Segoe UI Bold");
}
